import { FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, ApiError } from '@baaligames/shared-ui/lib/api';
import { useT } from '@baaligames/shared-ui/lib/i18n';
import Icon from '@baaligames/shared-ui/components/Icon';

export default function ForgotPassword() {
  const t = useT();
  const nav = useNavigate();
  const [identifier, setIdentifier] = useState('');
  // Default to Telegram — the email channel isn't wired to a live SMTP
  // sender yet, so defaulting to it silently broke password recovery
  // (2026-08-06 UX sweep). Telegram is the channel that actually delivers.
  const [channel, setChannel] = useState<'email'|'telegram'>('telegram');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setLoading(true); setError(null);
    try {
      const id = identifier.trim();
      const sendId = /^\d{10}$/.test(id) ? '+91' + id : id;
      await api.post('/auth/forgot-password', { identifier: sendId, channel });
      setSent(true);
      if (channel === 'telegram') {
        setTimeout(() => nav(`/reset-password?mode=otp&identifier=${encodeURIComponent(sendId)}`), 1500);
      }
    } catch (e) {
      setError((e as ApiError).message);
    } finally { setLoading(false); }
  }

  return (
    <div className="auth-shell">
      <div className="auth-card">
        <div className="auth-head">
          <div className="auth-head-badge">
            <Icon name="key" size={22} />
          </div>
          <h1 className="auth-h1">{t('forgot.title')}</h1>
          <p className="auth-sub">{t('forgot.subtitle')}</p>
        </div>

        {sent ? (
          <div className="auth-success">
            <div className="flex items-start gap-2">
              <Icon name="check_circle" />
              <div>
                {channel === 'email' ? t('forgot.sentEmail') : t('forgot.sentTelegram')}
                {channel === 'telegram' && <div className="mt-1 opacity-80">{t('forgot.redirecting')}</div>}
              </div>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div>
              <label className="label" htmlFor="fp-id">{t('forgot.idLabel')}</label>
              <div className="auth-field">
                <Icon name="person_search" className="auth-field-icon" />
                <input
                  id="fp-id"
                  className="auth-input"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  placeholder={t('forgot.idPlaceholder')}
                  maxLength={120}
                  required
                  autoFocus
                />
              </div>
            </div>

            <div>
              <label className="label">{t('forgot.sendVia')}</label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setChannel('email')}
                  disabled
                  title={t('forgot.emailUnavailable')}
                  className="auth-cta-ghost opacity-40 cursor-not-allowed"
                >
                  <span className="inline-flex items-center justify-center gap-1.5">
                    <Icon name="mail" /> {t('forgot.email')}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setChannel('telegram')}
                  className={`auth-cta-ghost ${channel === 'telegram' ? 'auth-cta-ghost--active' : ''}`}
                >
                  <span className="inline-flex items-center justify-center gap-1.5">
                    <Icon name="bolt" /> {t('forgot.telegram')}
                  </span>
                </button>
              </div>
              <p className="auth-field-help">{t('forgot.telegramHelp')}</p>
            </div>

            {error && <div className="auth-error" role="alert">{error}</div>}

            <button type="submit" className="auth-cta mt-1" disabled={loading}>
              {loading ? (
                <>
                  <Icon name="progress_activity" className="animate-spin" />
                  {t('common.sending')}
                </>
              ) : (
                <>{t('forgot.cta')} <Icon name="arrow_forward" /></>
              )}
            </button>
          </form>
        )}

        <div className="auth-foot">
          <Link to="/login" className="auth-link">← {t('forgot.back')}</Link>
        </div>
      </div>
    </div>
  );
}
